import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Breadcrumbs,
    Link,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminAgents from './AdminAgents';
import VoiceBots from './VoiceBots';
import AdminChatbots from './AdminChatbots';
import AdminClientProfile from './AdminClientProfile';
import Kbases from './Kbases';
import AdminClientBackups from './AdminClientBackups';

const SECTIONS = [
    { value: 'backups', labelKey: 'backups.title', fallback: 'Backups' },
    { value: 'voicebots', labelKey: 'voiceBots.title', fallback: 'VoiceBots' },
    { value: 'profile', labelKey: 'adminConfig.profile', fallback: 'Profile' },
    { value: 'chatbots', labelKey: 'adminConfig.chatbots', fallback: 'Chatbot' },
    { value: 'agents', labelKey: 'adminConfig.agents', fallback: 'Bot Agent' },
    { value: 'kbases', labelKey: 'adminConfig.kbases', fallback: 'KBase' },
];

function normalizeString(value) {
    return String(value ?? '').trim();
}

export default function AdminClientConfig({ initialSection }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clientId, section } = useParams();

    const normalizedInitial = normalizeString(initialSection);

    const activeSection = useMemo(() => {
        const s = normalizeString(section);
        if (s) return s;
        if (normalizedInitial) return normalizedInitial;
        return 'chatbots';
    }, [section, normalizedInitial]);

    useEffect(() => {
        const valid = SECTIONS.some((s) => s.value === activeSection);
        if (!valid && clientId) {
            navigate(`/admin/clients/${encodeURIComponent(clientId)}/config/chatbots`, { replace: true });
        }
    }, [activeSection, clientId, navigate]);

    const handleTabChange = (_event, nextValue) => {
        if (!clientId) return;
        navigate(`/admin/clients/${encodeURIComponent(clientId)}/config/${encodeURIComponent(nextValue)}`);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                    <Link component={RouterLink} underline="hover" color="inherit" to="/customer-list" sx={{ fontSize: 13 }}>
                        {t('adminConfig.clientList', 'Client List')}
                    </Link>
                    <Typography color="text.primary" sx={{ fontSize: 13 }}>
                        {t('adminConfig.breadcrumb', 'Config')}
                    </Typography>
                </Breadcrumbs>
                <Typography variant="h5" fontWeight="bold">
                    {t('adminConfig.title', 'Client Config')}{clientId ? `: ${clientId}` : ''}
                </Typography>
            </Box>

            <Tabs
                value={SECTIONS.some((s) => s.value === activeSection) ? activeSection : 'chatbots'}
                onChange={handleTabChange}
                sx={{
                    mb: 2,
                    minHeight: 42,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: 2,
                    },
                }}
            >
                {SECTIONS.map((s) => (
                    <Tab
                        key={s.value}
                        value={s.value}
                        label={t(s.labelKey, s.fallback)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minHeight: 42,
                            px: 2,
                            mr: 0,
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                            '&.Mui-selected': {
                                color: 'primary.main',
                            },
                        }}
                    />
                ))}
            </Tabs>

            {activeSection === 'kbases' && <Kbases embedded />}
            {activeSection === 'chatbots' && <AdminChatbots embedded />}
            {activeSection === 'agents' && <AdminAgents embedded />}
            {activeSection === 'voicebots' && <VoiceBots embedded />}
            {activeSection === 'profile' && <AdminClientProfile embedded />}
            {activeSection === 'backups' && <AdminClientBackups />}
        </Box>
    );
}
